import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import PageMeta from "../components/PageMeta";

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [status, setStatus] = useState(token ? "loading" : "missing");
  const [message, setMessage] = useState("");
  const ran = useRef(false);

  useEffect(() => {
    if (!token || ran.current) return;
    ran.current = true; // the link works only once, so never send it twice
    api
      .post("/auth/verify-email", { token })
      .then(() => setStatus("success"))
      .catch((err) => {
        const detail = err && err.response && err.response.data && err.response.data.detail;
        setMessage(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
        setStatus("error");
      });
  }, [token]);

  return (
    <div className="pt-32 pb-24 px-6">
      <PageMeta
        title="Confirm your email | MediaCrypto"
        description="Confirm your email address to finish setting up your MediaCrypto account."
        path="/verify-email"
        noindex
      />
      <div className="max-w-md mx-auto bg-[var(--bg-panel)] border border-[var(--border-soft)] p-8 text-center">
        {status === "loading" && (
          <>
            <div className="mx-auto w-8 h-8 border-2 border-yellow-400 border-t-transparent rounded-full animate-spin" />
            <h1 className="mt-5 font-display font-bold text-2xl text-[var(--text-primary)]">
              Confirming your email
            </h1>
            <p className="mt-2 text-sm text-[var(--text-secondary)]">One moment.</p>
          </>
        )}

        {status === "success" && (
          <>
            <div className="mx-auto w-12 h-12 rounded-full bg-green-500/15 text-green-400 flex items-center justify-center text-2xl">
              ✓
            </div>
            <h1 className="mt-5 font-display font-bold text-2xl text-[var(--text-primary)]">
              Email confirmed
            </h1>
            <p className="mt-2 text-sm text-[var(--text-secondary)]">
              Your account is verified. You can now use all member features.
            </p>
            <div className="mt-6 flex flex-col gap-3">
              <Link
                to="/trading-simulator"
                className="bg-yellow-400 text-black font-bold uppercase tracking-wide text-sm px-6 py-3 hover:bg-yellow-300 transition"
              >
                Open the simulator
              </Link>
              <Link
                to="/"
                className="font-mono text-xs uppercase tracking-widest text-[var(--text-secondary)] hover:text-yellow-400 transition"
              >
                Back to home
              </Link>
            </div>
            <p className="mt-6 text-xs text-[var(--text-tertiary)]">
              If you were already logged in, refresh the page or log in again to see your verified status.
            </p>
          </>
        )}

        {(status === "error" || status === "missing") && (
          <>
            <div className="mx-auto w-12 h-12 rounded-full bg-red-500/15 text-red-400 flex items-center justify-center text-2xl">
              !
            </div>
            <h1 className="mt-5 font-display font-bold text-2xl text-[var(--text-primary)]">
              Link not valid
            </h1>
            <p className="mt-2 text-sm text-[var(--text-secondary)]">
              {status === "missing"
                ? "This page needs the link from your confirmation email."
                : message}
            </p>
            <div className="mt-6 flex flex-col gap-3">
              <Link
                to="/login"
                className="bg-yellow-400 text-black font-bold uppercase tracking-wide text-sm px-6 py-3 hover:bg-yellow-300 transition"
              >
                Log in to request a new link
              </Link>
              <Link
                to="/"
                className="font-mono text-xs uppercase tracking-widest text-[var(--text-secondary)] hover:text-yellow-400 transition"
              >
                Back to home
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}